import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useMemo } from 'react';

import type { OptionType } from '@/api/common';
import { useVendorList } from '@/api/vendor';
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxStatus,
  type ComboboxProps,
} from '@/components/Form/pickers';

type Props = ComboboxProps<OptionType, false> & {
  className?: string;
  disabled?: boolean;
  placeholder?: string;
  disabledVendorIds?: string[];
  channelId?: string;
  onBlur?: () => void;
};

const VendorSelect = ({
  value,
  disabled = false,
  size = 'large',
  className,
  channelId,
  disabledVendorIds,
  placeholder = t`Select Vendor...`,
  onBlur,
  ...rest
}: Props) => {
  const { vendors, isLoading } = useVendorList({ channelId });

  const uniqueVendors = useMemo<OptionType[]>(() => {
    return (
      vendors?.map((vendor) => ({
        id: vendor.id,
        name: vendor.name,
        disabled: (disabledVendorIds || []).includes(vendor.id),
      })) ?? []
    );
  }, [vendors, disabledVendorIds]);

  return (
    <Combobox
      items={uniqueVendors}
      itemToStringValue={(item) => item.id}
      itemToStringLabel={(item) => item.name ?? ''}
      isItemEqualToValue={(item, selected) => item.id === selected.id}
      disabled={disabled}
      isLoading={isLoading}
      size={size}
      value={value ?? null}
      {...rest}
    >
      <ComboboxInput className={className} placeholder={placeholder} onBlur={onBlur} />
      <ComboboxContent>
        <ComboboxStatus />
        <ComboboxEmpty>
          <Trans>No vendors were found...</Trans>
        </ComboboxEmpty>
        <ComboboxList>
          {(item: OptionType) => (
            <ComboboxItem key={item.id} value={item} disabled={item.disabled}>
              {item.name}
            </ComboboxItem>
          )}
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  );
};

export default VendorSelect;
